import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { adminService, type CreateTenantPayload } from '../../services/admin';
import {
  AGENCY_TRIAL_CREDITS,
  AGENCY_PLANS,
  TIER_LIMITS,
  type BillingMode,
  type FeatureFlags,
  type QuotaOverrides,
  type Tier,
} from '@traaker/shared';

const TIERS: Tier[] = ['basic', 'pro', 'elite', 'enterprise', 'agency'];

// Quota dimensions an enterprise contract can override individually.
const QUOTA_KEYS: Array<keyof QuotaOverrides> = [
  'brands',
  'zones',
  'users',
  'competitors',
  'pagesPerAudit',
  'pagesPerDomain',
  'apiCallsPerDay',
  'concurrentAgents',
  'socialIntegrations',
  'prompts',
  'brandNarrativeRunsPerMonth',
  'brandAuthorityRunsPerMonth',
  'brandReferralRunsPerMonth',
  // Product module — both default to 0 on every non-agency tier, so setting
  // these two IS what unlocks the module's capacity for an enterprise deal
  // (alongside the productModuleEnabled flag).
  'catalog_items',
  'product_reviews',
  // Cibles — establishments is tenant-wide (Geoapify geocoding budget, default 100).
  // target_signals is per-brand (search Lambda memory cap, default 500k). D13, D34.
  'establishments',
  'target_signals',
];

// Read-only summary keys shown for self-serve tiers (so the admin sees what the
// tenant will get without being able to change it). `prompts` lives on the metric
// registry, not TierLimits, so this is a loose string list read defensively.
const SUMMARY_KEYS: string[] = [
  'brands',
  'zones',
  'users',
  'competitors',
  'pagesPerAudit',
];

const FLAG_LABELS: Array<{ key: keyof FeatureFlags; labelKey: string }> = [
  { key: 'tasksEnabled', labelKey: 'admin.create.flag.tasks' },
  { key: 'newsWatchEnabled', labelKey: 'admin.create.flag.newsWatch' },
  { key: 'competitorsEnabled', labelKey: 'admin.create.flag.competitors' },
  { key: 'reportsEnabled', labelKey: 'admin.create.flag.reports' },
  { key: 'productModuleEnabled', labelKey: 'admin.create.flag.productModule' },
  { key: 'conversationsEnabled', labelKey: 'admin.create.flag.conversations' },
];

// BP measurement engines (internal run-path ids). Enterprise selects its
// tenant-wide set here; it runs automatically every audit. Secondary engines
// (a model + a web-search harness we own) are explicit opt-in; primaries are
// pre-checked so the common case is one save.
const ENGINE_OPTIONS: Array<{ id: string; label: string; secondary?: boolean }> = [
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'openai', label: 'ChatGPT' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'claude', label: 'Claude' },
  { id: 'mistral', label: 'Mistral', secondary: true },
  { id: 'ai_overview', label: 'AI Overview', secondary: true },
  // Conversations only — no BP engine has this id. Listed because an `engines`
  // override REPLACES the allowlist: omit it here and saving an override silently
  // rejects every persona carrying a `google` cell.
  { id: 'google', label: 'Google AI Overview (conversations)', secondary: true },
];
const DEFAULT_ENGINE_IDS = ENGINE_OPTIONS.filter((e) => !e.secondary).map((e) => e.id);

/**
 * Tier-aware tenant creation. The tier select drives what's editable:
 *  - enterprise → custom quota overrides + feature flags + custom price
 *  - basic/pro/elite → tier defaults shown read-only (no overrides)
 *  - agency → credit terms (allocation / price / starting grant)
 * Only the email is strictly required besides org name + domain; the owner then
 * receives an email to create their account / log in (existing flow).
 */
export default function AdminTenantCreate() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [tier, setTier] = useState<Tier>('pro');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  // Default UI language for the owner — seeds onboarding + the whole app language on
  // first login (user can change it later via the profile dropdown). Only fr/en are built.
  const [ownerUiLanguage, setOwnerUiLanguage] = useState<'fr' | 'en'>('fr');
  // Billing mode is always Stripe — manual mode was retired.
  const billingMode: BillingMode = 'stripe';

  // Trial (non-agency). Default 7 days for basic, 0 (pay-first) otherwise.
  const [trialDays, setTrialDays] = useState<string>('7');

  // Enterprise-only custom config.
  const [quotaOverrides, setQuotaOverrides] = useState<Record<string, string>>({});
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({
    newsWatchEnabled: false,
    competitorsEnabled: false,
    tasksEnabled: false,
    reportsEnabled: false,
    productModuleEnabled: false,
  });
  const [contractAmountEuros, setContractAmountEuros] = useState<string>('');
  const [contractInterval, setContractInterval] = useState<'month' | 'year'>('month');
  const [contractNotes, setContractNotes] = useState('');
  // Enterprise tenant-wide BP engine selection (runs automatically every audit).
  const [enabledEngines, setEnabledEngines] = useState<string[]>(DEFAULT_ENGINE_IDS);

  // Agency credit terms. `monthlyAllocation` is NOT collected here — it's set from
  // the Stripe subscription's Price metadata when the agency subscribes (the webhook
  // overwrites it), so we seed 0.
  const [agencyPricePerCreditCents, setAgencyPricePerCreditCents] = useState<string>('');
  // Default the starting (trial) grant to the self-signup default so an
  // admin-created agency lands with the same credit balance unless overridden.
  const [agencyStartingCredits, setAgencyStartingCredits] = useState<string>(
    String(AGENCY_TRIAL_CREDITS),
  );
  // Pay-first agency: seed `pending` + force Stripe checkout before the app
  // unlocks (no credit-trial). Requires picking an Agency plan (S/M/L) to charge.
  const [agencyPayFirst, setAgencyPayFirst] = useState(false);
  const [agencyPlanKey, setAgencyPlanKey] = useState<string>(AGENCY_PLANS[0].key);

  const isAgency = tier === 'agency';
  const isEnterprise = tier === 'enterprise';
  const isSelfServe = tier === 'basic' || tier === 'pro' || tier === 'elite';
  const tierDefaults = useMemo(() => TIER_LIMITS[tier], [tier]);

  const handleTierChange = (next: Tier) => {
    setTier(next);
    // Sensible trial default per tier (basic is the time-trial tier).
    setTrialDays(next === 'basic' ? '7' : '0');
  };

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const qo: QuotaOverrides = {};
      if (isEnterprise) {
        for (const k of QUOTA_KEYS) {
          const raw = quotaOverrides[k];
          if (raw != null && raw !== '') {
            const n = Number(raw);
            if (!Number.isNaN(n)) (qo as Record<string, number>)[k] = n;
          }
        }
      }
      const payload: CreateTenantPayload = {
        name,
        domain,
        tier,
        ownerEmail,
        ownerName: ownerName || undefined,
        ownerUiLanguage,
        settings: isEnterprise
          ? {
              quotaOverrides: Object.keys(qo).length ? qo : undefined,
              featureFlags,
              enabledEngines: enabledEngines.length ? enabledEngines : undefined,
            }
          : undefined,
        billing: {
          mode: billingMode,
          amountCents:
            isEnterprise && contractAmountEuros
              ? Math.round(Number(contractAmountEuros) * 100)
              : undefined,
          interval: contractInterval,
          notes: contractNotes || undefined,
        },
        ...(isAgency
          ? {}
          : { trialDays: trialDays ? Number(trialDays) : 0 }),
        // Always send the agency block for an agency tenant — gating it on
        // monthlyAllocation silently dropped `startingCredits` when the admin
        // left the allocation blank, so the trial grant never ran.
        ...(isAgency
          ? {
              agency: {
                // Set from the Stripe subscription on subscribe (webhook overwrites).
                // For pay-first the server seeds it from the chosen plan.
                monthlyAllocation: 0,
                pricePerCreditCents: agencyPricePerCreditCents
                  ? Number(agencyPricePerCreditCents)
                  : undefined,
                // Trial credits are ignored server-side in pay-first mode.
                startingCredits: agencyStartingCredits
                  ? Number(agencyStartingCredits)
                  : undefined,
                ...(agencyPayFirst
                  ? { payFirst: true, planKey: agencyPlanKey }
                  : {}),
              },
            }
          : {}),
      };
      const { tenant: created } = await adminService.createTenant(payload);
      navigate(`/admin/tenants/${created.tenantId}`);
    } catch (err: any) {
      setError(err.message || t('admin.create.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/tenants')} size="small">
          {t('admin.create.back')}
        </Button>
        <Typography variant="h5" fontWeight={700}>{t('admin.create.title')}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack spacing={3}>
        {/* Basics */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>{t('admin.create.infoTitle')}</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField label={t('admin.create.orgName')} value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" required />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField label={t('admin.create.domain')} value={domain} onChange={(e) => setDomain(e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('admin.create.tier')}</InputLabel>
                  <Select label={t('admin.create.tier')} value={tier} onChange={(e) => handleTierChange(e.target.value as Tier)}>
                    {TIERS.map((t) => (
                      <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField label={t('admin.create.ownerEmail')} value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} fullWidth size="small" required />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField label={t('admin.create.ownerName')} value={ownerName} onChange={(e) => setOwnerName(e.target.value)} fullWidth size="small" />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('admin.create.ownerUiLanguage')}</InputLabel>
                  <Select
                    label={t('admin.create.ownerUiLanguage')}
                    value={ownerUiLanguage}
                    onChange={(e) => setOwnerUiLanguage(e.target.value as 'fr' | 'en')}
                  >
                    <MenuItem value="fr">Français</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                  <FormHelperText>{t('admin.create.ownerUiLanguageHelp')}</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Trans i18nKey="admin.create.ownerNote" components={{ b: <strong /> }} />
            </Alert>
          </CardContent>
        </Card>

        {/* Trial / credits */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {isAgency ? t('admin.create.agencyCreditsTitle') : t('admin.create.trialTitle')}
            </Typography>
            {!isAgency && (
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth size="small" type="number" label={t('admin.create.trialDays')}
                    value={trialDays}
                    onChange={(e) => setTrialDays(e.target.value)}
                    helperText={t('admin.create.trialDaysHelp')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Typography variant="body2" color="text.secondary">
                    <Trans i18nKey="admin.create.trialNote" components={{ b: <strong /> }} />
                  </Typography>
                </Grid>
              </Grid>
            )}
            {isAgency && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={agencyPayFirst}
                        onChange={(e) => setAgencyPayFirst(e.target.checked)}
                      />
                    }
                    label="Paiement à la création (parcours de commande)"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Activé : le compte est créé en <strong>pending</strong> et redirigé vers le paiement
                    Stripe dès la première connexion (email → connexion → paiement → première marque).
                    Aucun crédit d’essai. Désactivé : essai crédité classique, accès immédiat.
                  </Typography>
                </Grid>
                {agencyPayFirst && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Offre Agence</InputLabel>
                      <Select
                        label="Offre Agence"
                        value={agencyPlanKey}
                        onChange={(e) => setAgencyPlanKey(e.target.value)}
                      >
                        {AGENCY_PLANS.map((p) => (
                          <MenuItem key={p.key} value={p.key}>
                            {p.label} — {p.priceEur.toLocaleString('fr-FR')} € / mois ·{' '}
                            {p.monthlyAllocation.toLocaleString('fr-FR')} crédits
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth size="small" label={t('admin.create.pricePerCredit')} type="number"
                    value={agencyPricePerCreditCents}
                    onChange={(e) => setAgencyPricePerCreditCents(e.target.value)}
                    helperText={t('admin.create.pricePerCreditHelp')}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth size="small" label={t('admin.create.startingCredits')} type="number"
                    value={agencyStartingCredits}
                    onChange={(e) => setAgencyStartingCredits(e.target.value)}
                    disabled={agencyPayFirst}
                    helperText={
                      agencyPayFirst
                        ? 'Ignoré en mode paiement à la création (compte pending, crédits non utilisables).'
                        : t('admin.create.startingCreditsHelp', { count: AGENCY_TRIAL_CREDITS })
                    }
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Enterprise: custom quotas + flags + price */}
        {isEnterprise && (
          <>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{t('admin.create.customQuotasTitle')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('admin.create.quotasHelp')}
                </Typography>
                <Grid container spacing={2}>
                  {QUOTA_KEYS.map((k) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={k}>
                      <TextField
                        label={k}
                        type="number"
                        value={quotaOverrides[k] || ''}
                        onChange={(e) => setQuotaOverrides({ ...quotaOverrides, [k]: e.target.value })}
                        placeholder={String((tierDefaults as any)?.[k] ?? '')}
                        fullWidth
                        size="small"
                        helperText={t('admin.create.defaultValue', { value: (tierDefaults as any)?.[k] ?? '—' })}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>{t('admin.create.featuresTitle')}</Typography>
                <Stack>
                  {FLAG_LABELS.map(({ key, labelKey }) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Switch
                          checked={!!featureFlags[key]}
                          onChange={(e) => setFeatureFlags({ ...featureFlags, [key]: e.target.checked })}
                        />
                      }
                      label={t(labelKey)}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{t('admin.create.enginesTitle')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('admin.create.enginesHelp')}
                </Typography>
                <Stack>
                  {ENGINE_OPTIONS.map((e) => (
                    <FormControlLabel
                      key={e.id}
                      control={
                        <Switch
                          checked={enabledEngines.includes(e.id)}
                          onChange={(ev) =>
                            setEnabledEngines((prev) =>
                              ev.target.checked
                                ? [...prev, e.id]
                                : prev.filter((x) => x !== e.id),
                            )
                          }
                        />
                      }
                      label={e.label}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>{t('admin.create.customPriceTitle')}</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label={t('admin.create.negotiatedAmount')} type="number" fullWidth size="small"
                      value={contractAmountEuros}
                      onChange={(e) => setContractAmountEuros(e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('admin.create.interval')}</InputLabel>
                      <Select label={t('admin.create.interval')} value={contractInterval} onChange={(e) => setContractInterval(e.target.value as 'month' | 'year')}>
                        <MenuItem value="month">{t('admin.create.intervalMonthly')}</MenuItem>
                        <MenuItem value="year">{t('admin.create.intervalYearly')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={t('admin.create.contractNotes')} fullWidth size="small" multiline minRows={2}
                      value={contractNotes}
                      onChange={(e) => setContractNotes(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </>
        )}

        {/* Self-serve: read-only tier defaults */}
        {isSelfServe && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                {t('admin.create.tierLimitsTitle')} <span style={{ textTransform: 'capitalize' }}>{tier}</span>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <Trans i18nKey="admin.create.tierLimitsHelp" components={{ b: <strong /> }} />
              </Typography>
              <Grid container spacing={2}>
                {SUMMARY_KEYS.map((k) => (
                  <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k}>
                    <Typography variant="caption" color="text.secondary">{k}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {(tierDefaults as any)?.[k] === -1 ? '∞' : (tierDefaults as any)?.[k] ?? '—'}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => navigate('/admin/tenants')}>{t('admin.create.cancel')}</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={saving || !name || !ownerEmail}
            onClick={handleCreate}
          >
            {t('admin.create.submit')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
